import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Search, Printer, MessageCircle } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/site/StatusBadge";
import { AccountSettings } from "@/components/site/AccountSettings";
import { PaymentsPanel } from "@/components/site/PaymentsPanel";
import { BookingsPanel } from "@/components/site/BookingsPanel";
import { ReviewsPanel } from "@/components/site/Reviews";
import { AnalyticsPanel } from "@/components/site/AnalyticsPanel";
import { BlogAdmin } from "@/components/site/BlogAdmin";
import { CallsPanel } from "@/components/site/CallsPanel";
import { ClassesAdmin } from "@/components/site/ClassesAdmin";
import { RetreatsAdmin } from "@/components/site/RetreatsAdmin";
import { JobsAdmin, CommunityAdmin } from "@/components/site/CommunityAdmin";
import { AvenueSettingsAdmin } from "@/components/avenues/AvenueParts";
import { Spinner } from "@/components/site/ProtectedRoute";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { APP_STAGES, COURSE_FORMATS } from "@/lib/constants";

const stageLabel = (v) => APP_STAGES.find((s) => s.value === v)?.label || v;

function Stat({ label, value, testId, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-2 font-display text-3xl font-bold ${accent || "text-[#0B2A4A]"}`}>{value ?? "–"}</p>
    </div>
  );
}

export function decideApplication(id, action) {
  return api.post(`/admin/applications/${id}/${action}`).then(({ data }) => {
    toast.success(`Application ${data.status}${data.email_sent ? " · email sent" : ""}`);
    return data;
  }).catch((e) => toast.error(formatApiError(e)));
}

function Applications({ apps, reload }) {
  const pending = apps.filter((a) => a.status === "pending");
  const others = apps.filter((a) => a.status !== "pending");
  const act = async (id, action) => { await decideApplication(id, action); reload(); };
  const Row = ({ a }) => (
    <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 flex flex-col lg:flex-row lg:items-center gap-4 justify-between" data-testid="admin-application-row">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/host/people/${encodeURIComponent(a.email)}`} className="font-semibold text-[#0B2A4A] hover:text-[#1A9AA8]" data-testid="admin-application-name">{a.name}</Link>
          <StatusBadge status={a.status} testId="admin-application-status" />
          {a.payment
            ? <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${a.payment.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`} data-testid="admin-application-paid">{a.payment.status === "confirmed" ? "Paid" : "Paid — check PayPal"} ${a.payment.amount}</span>
            : <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500" data-testid="admin-application-unpaid">No payment yet</span>}
        </div>
        <p className="text-sm text-slate-500">{a.email} · {stageLabel(a.stage)}{a.format ? ` · ${COURSE_FORMATS.find((s) => s.value === a.format)?.label}` : ""} · applied {fmtDate(a.created_at)}</p>
        <p className="mt-2 text-sm text-slate-700 line-clamp-2">{a.goals}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        {a.status !== "approved" && <button onClick={() => act(a.id, "approve")} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700" data-testid="admin-approve-app-btn"><Check className="h-4 w-4" /> Approve</button>}
        {a.status !== "declined" && <button onClick={() => act(a.id, "decline")} className="inline-flex items-center gap-1.5 rounded-xl border border-rose-300 text-rose-700 px-4 py-2 text-sm font-semibold hover:bg-rose-50" data-testid="admin-decline-app-btn"><X className="h-4 w-4" /> Decline</button>}
      </div>
    </div>
  );
  return (
    <div className="flex flex-col gap-4">
      <h2 className="font-display text-lg font-semibold text-[#0B2A4A]">Waiting for review ({pending.length})</h2>
      {pending.length === 0 && <p className="text-sm text-slate-500" data-testid="admin-no-pending">No pending applications.</p>}
      {pending.map((a) => <Row key={a.id} a={a} />)}
      {others.length > 0 && <h2 className="mt-6 font-display text-lg font-semibold text-[#0B2A4A]">Reviewed</h2>}
      {others.map((a) => <Row key={a.id} a={a} />)}
    </div>
  );
}

function People({ people }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();
  const rows = useMemo(() => people.filter((p) => {
    const hit = `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase());
    if (filter === "leads") return hit && p.lead_count;
    if (filter === "students") return hit && p.application_status === "approved";
    if (filter === "pending") return hit && p.application_status === "pending";
    return hit;
  }), [people, q, filter]);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><Input className="pl-9 bg-white" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-people-search" /></div>
        <div className="flex gap-1.5 flex-wrap">
          {["all", "leads", "pending", "students"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-sm font-semibold capitalize ${filter === f ? "bg-[#0B2A4A] text-[#F5ECD7]" : "bg-white border border-[#0B2A4A]/15"}`} data-testid={`admin-filter-${f}`}>{f}</button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-[#0B2A4A]/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#FAF6EE] text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Person</th><th className="px-4 py-3">Journey</th><th className="px-4 py-3">Lead</th><th className="px-4 py-3">Course</th><th className="px-4 py-3">Last activity</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No people yet.</td></tr>}
            {rows.map((p) => (
              <tr key={p.email} onClick={() => nav(`/host/people/${encodeURIComponent(p.email)}`)} className="border-t border-[#0B2A4A]/5 hover:bg-[#FAF6EE] cursor-pointer" data-testid="admin-lead-row">
                <td className="px-4 py-3"><p className="font-semibold text-[#0B2A4A] flex items-center gap-2">{p.name}{p.unread ? <span className="inline-flex items-center gap-1 text-[10px] bg-[#F08A3C] text-white rounded-full px-2 py-0.5"><MessageCircle className="h-3 w-3" />{p.unread}</span> : null}</p><p className="text-xs text-slate-500">{p.email}</p></td>
                <td className="px-4 py-3 text-slate-600 max-w-[220px]">{p.interest && p.interest !== "Digital Nomad Nicaragua" && <span className="block text-[10px] font-bold uppercase tracking-wider text-[#D96F28]" data-testid="admin-lead-interest">{p.interest}</span>}<span className="block truncate">{p.journey_stage || "—"}</span>{p.add_ons?.length ? <span className="block truncate text-xs text-[#1A9AA8]" data-testid="admin-lead-addons">+ {p.add_ons.join(", ")}</span> : null}</td>
                <td className="px-4 py-3">{p.lead_status ? <StatusBadge status={p.lead_status} /> : <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3">{p.application_status ? <StatusBadge status={p.application_status} /> : <span className="text-xs text-slate-400">{p.has_account ? "Account, no application" : "No account"}</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-500 font-mono-ui">{fmtDate(p.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function HostDashboard() {
  useSeo({ title: "Host dashboard", path: "/host", noindex: true });
  const [stats, setStats] = useState(null);
  const [people, setPeople] = useState(null);
  const [apps, setApps] = useState([]);
  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/people").then((r) => setPeople(r.data));
    api.get("/admin/applications").then((r) => setApps(r.data));
  };
  useEffect(load, []);
  if (!people) return <Spinner />;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14" data-testid="host-dashboard">
      <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">HOST ONLY</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2A4A]">Host dashboard</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/print/host-plan" className="inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 bg-white px-4 py-2 text-sm font-semibold" data-testid="host-print-plan-btn"><Printer className="h-4 w-4" /> Print the host lesson plan</Link>
          <Link to="/print/worksheets?mode=blank" className="inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 bg-white px-4 py-2 text-sm font-semibold" data-testid="host-print-blank-btn"><Printer className="h-4 w-4" /> Blank worksheets</Link>
          <Link to="/course/day/1" className="inline-flex items-center gap-2 rounded-xl bg-[#1A9AA8] text-white px-4 py-2 text-sm font-semibold" data-testid="host-view-lessons-btn">Teach from lessons</Link>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Total leads" value={stats?.leads} testId="stat-leads" />
        <Stat label="New leads" value={stats?.new_leads} testId="stat-new-leads" accent="text-[#F08A3C]" />
        <Stat label="Pending applications" value={stats?.pending} testId="stat-pending" accent="text-[#F08A3C]" />
        <Stat label="Approved students" value={stats?.approved} testId="stat-approved" accent="text-emerald-600" />
        <Stat label="Unread messages" value={stats?.unread_messages} testId="stat-unread" accent="text-[#1A9AA8]" />
      </div>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "applications"} className="mt-10">
        <TabsList className="bg-[#0B2A4A]/5 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="applications" data-testid="host-tab-applications">Applications</TabsTrigger>
          <TabsTrigger value="people" data-testid="host-tab-people">Leads & students</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="host-tab-bookings">Schedules</TabsTrigger>
          <TabsTrigger value="calls" data-testid="host-tab-calls">Free calls</TabsTrigger>
          <TabsTrigger value="classes" data-testid="host-tab-classes">Classes</TabsTrigger>
          <TabsTrigger value="retreats" data-testid="host-tab-retreats">Retreat dates</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="host-tab-jobs">Jobs</TabsTrigger>
          <TabsTrigger value="community" data-testid="host-tab-community">Community</TabsTrigger>
          <TabsTrigger value="online-courses" data-testid="host-tab-online-courses">Online courses</TabsTrigger>
          <TabsTrigger value="blog" data-testid="host-tab-blog">Blog</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="host-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="host-tab-reviews">Reviews</TabsTrigger>
          <TabsTrigger value="payments" data-testid="host-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="account" data-testid="host-tab-account">Account</TabsTrigger>
        </TabsList>
        <TabsContent value="applications" className="mt-6"><Applications apps={apps} reload={load} /></TabsContent>
        <TabsContent value="people" className="mt-6"><People people={people} /></TabsContent>
        <TabsContent value="bookings" className="mt-6"><BookingsPanel /></TabsContent>
        <TabsContent value="calls" className="mt-6"><CallsPanel /></TabsContent>
        <TabsContent value="classes" className="mt-6"><ClassesAdmin /></TabsContent>
        <TabsContent value="retreats" className="mt-6"><RetreatsAdmin /></TabsContent>
        <TabsContent value="jobs" className="mt-6"><JobsAdmin /></TabsContent>
        <TabsContent value="community" className="mt-6"><CommunityAdmin /></TabsContent>
        <TabsContent value="online-courses" className="mt-6"><AvenueSettingsAdmin /></TabsContent>
        <TabsContent value="blog" className="mt-6"><BlogAdmin /></TabsContent>
        <TabsContent value="analytics" className="mt-6"><AnalyticsPanel /></TabsContent>
        <TabsContent value="reviews" className="mt-6"><ReviewsPanel /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsPanel /></TabsContent>
        <TabsContent value="account" className="mt-6"><AccountSettings /></TabsContent>
      </Tabs>
    </section>
  );
}
